import React from 'react'
import {media} from 'styled-bootstrap-grid'
import styled from 'styled-components'
import {palette} from 'styled-tools'
import BankCardLineIcon from 'remixicon-react/BankCardLineIcon'
import FileListLineIcon from 'remixicon-react/FileListLineIcon'
import TruckLineIcon from 'remixicon-react/TruckLineIcon'
import UserLineIcon from 'remixicon-react/UserLineIcon'
import {format as formatSSN} from 'kennitala'
import {isNotNull} from 'src/utils/isNotNull'
import type {Order} from 'src/api/saleor/generated'
import {PaymentChargeStatusEnum} from 'src/api/saleor/generated'
import {theme} from 'src/themes'
import {formatPhoneNumber} from 'src/utils/strings'
import {Title7} from '../typography'

interface Props {
  children?: React.ReactNode
  order: Order
}

const paymentMap: Record<string, string> = {
  'reon.payments.valitor_pay': 'Greiðsla með korti',
  'mirumee.payments.dummy': 'Í reikning',
}

const Card = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 16px;
  background-color: ${palette('white')};
  border: 1px solid ${palette('black10')};

  ${media.md`
    padding: 48px;
  `}
`

const InfoSectionWrapper = styled.div`
  display: flex;
  padding: 24px;
  width: 100%;
  border-bottom: 1px solid ${palette('black20')};
  margin-bottom: 16px;

  ${media.md`
    width: 50%;
    padding: 48px;
    margin-bottom: 56px;
  `}
`

const IconBox = styled.div`
  margin-right: 24px;
`

const InfoLineWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
const InfoLine = styled.div`
  font-size: 1rem;
  line-height: 1.8;
`

export default function CheckoutCompleteInfoCard({
  children,
  order,
}: Props): JSX.Element {
  const shippingMethod = order?.shippingMethod
  const payments = order?.payments
  const payment = payments
    ?.filter(isNotNull)
    .find((p) => p.chargeStatus === PaymentChargeStatusEnum.FullyCharged)

  const creditCard =
    payment?.gateway === 'reon.payments.valitor_pay' && payment?.creditCard

  const shippingAddressName =
    order?.shippingAddress?.firstName || order?.shippingAddress?.companyName
  const billingAddressName =
    order?.billingAddress?.firstName || order?.billingAddress?.companyName

  return (
    <Card>
      {children}

      <InfoSectionWrapper>
        <IconBox>
          <UserLineIcon size={32} color={theme.palette.primary} />
        </IconBox>
        <InfoLineWrapper>
          <Title7 withGutter>Móttakandi</Title7>
          <InfoLine>{shippingAddressName}</InfoLine>
          {order?.shippingAddress?.lastName ? (
            <InfoLine>{formatSSN(order?.shippingAddress?.lastName)}</InfoLine>
          ) : null}
          {order?.shippingAddress?.phone ? (
            <InfoLine>
              {formatPhoneNumber(order?.shippingAddress?.phone)}
            </InfoLine>
          ) : null}
          <InfoLine>{order?.shippingAddress?.streetAddress1},</InfoLine>
          <InfoLine>
            {order?.shippingAddress?.postalCode} {order?.shippingAddress?.city}
          </InfoLine>
        </InfoLineWrapper>
      </InfoSectionWrapper>

      <InfoSectionWrapper>
        <IconBox>
          <FileListLineIcon size={32} color={theme.palette.primary} />
        </IconBox>
        <InfoLineWrapper>
          <Title7 withGutter>Greiðandi</Title7>
          <InfoLine>{billingAddressName}</InfoLine>
          {order?.billingAddress?.lastName ? (
            <InfoLine>{formatSSN(order?.billingAddress?.lastName)}</InfoLine>
          ) : null}
          {order?.billingAddress?.phone ? (
            <InfoLine>
              {formatPhoneNumber(order?.billingAddress?.phone)}
            </InfoLine>
          ) : null}
          <InfoLine>{order?.billingAddress?.streetAddress1},</InfoLine>
          <InfoLine>
            {order?.billingAddress?.postalCode} {order?.billingAddress?.city}
          </InfoLine>
        </InfoLineWrapper>
      </InfoSectionWrapper>

      <InfoSectionWrapper>
        <IconBox>
          <TruckLineIcon size={32} color={theme.palette.primary} />
        </IconBox>
        <InfoLineWrapper>
          <Title7 withGutter>Afhending</Title7>
          <InfoLine>{shippingMethod?.name}</InfoLine>
          {shippingMethod?.name === 'Dropp Heimsending' && (
            <>
              <InfoLine>{shippingAddressName}</InfoLine>
              {order?.shippingAddress?.lastName ? (
                <InfoLine>
                  {formatSSN(order?.shippingAddress?.lastName)}
                </InfoLine>
              ) : null}
              {order?.shippingAddress?.phone ? (
                <InfoLine>
                  {formatPhoneNumber(order?.shippingAddress?.phone)}
                </InfoLine>
              ) : null}
              <InfoLine>{order?.shippingAddress?.streetAddress1},</InfoLine>
              <InfoLine>
                {order?.shippingAddress?.postalCode}{' '}
                {order?.shippingAddress?.city}
              </InfoLine>
            </>
          )}
        </InfoLineWrapper>
      </InfoSectionWrapper>

      <InfoSectionWrapper>
        <IconBox>
          <BankCardLineIcon size={32} color={theme.palette.primary} />
        </IconBox>
        <InfoLineWrapper>
          <Title7 withGutter>Greiðsla</Title7>
          {payment?.gateway && paymentMap[payment.gateway] ? (
            <InfoLine>{paymentMap[payment?.gateway]}</InfoLine>
          ) : null}
          <InfoLine>
            {creditCard && `${creditCard?.brand} - ${creditCard?.lastDigits}`}
          </InfoLine>
        </InfoLineWrapper>
      </InfoSectionWrapper>
    </Card>
  )
}
